import { Component } from '@theme/component';

/**
 * "Load more" for a paginated list.
 *
 * The button is a real link to the next page, so without JavaScript it simply opens that page.
 * With it, the next page is fetched, its items are appended to the list, and the button's link and
 * the "Showing X of Y" line are taken from the page that came back. When there is no next page the
 * button goes away.
 *
 * @extends {Component<{
 *   list: HTMLElement,
 *   more: HTMLElement,
 *   button: HTMLAnchorElement,
 *   progress: HTMLElement,
 * }>}
 */
class OudhLoadMore extends Component {
  connectedCallback() {
    super.connectedCallback();
    // The same remembered choice the product catalogue uses.
    try {
      const saved = localStorage.getItem('oudh-catalog-cols');
      if (saved === '3' || saved === '4') this.#applyColumns(saved);
    } catch {
      // Storage unavailable: keep the section's default.
    }
  }

  /**
   * Items per row (3 or 4) from the switch above the grid.
   *
   * @param {Event} event
   */
  setColumns(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-cols]') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.cols) return;

    this.#applyColumns(button.dataset.cols);
    try {
      localStorage.setItem('oudh-catalog-cols', button.dataset.cols);
    } catch {
      // Private mode: the choice lasts for this page only.
    }
  }

  /** @param {string} cols */
  #applyColumns(cols) {
    this.setAttribute('data-cols', cols);
    for (const button of this.querySelectorAll('.oudh-density__btn')) {
      button.setAttribute('aria-pressed', String(button instanceof HTMLElement && button.dataset.cols === cols));
    }
  }

  /**
   * @param {Event} event
   */
  async load(event) {
    event.preventDefault();

    const { list, more, button, progress } = this.refs;
    const href = button.getAttribute('href');

    // A hand-picked list is already on the page, hidden past the first batch: just show more of it.
    if (!href) {
      const batch = Number(button.dataset.batch) || 12;
      const hidden = [...list.querySelectorAll(':scope > [hidden]')];
      for (const item of hidden.slice(0, batch)) item.hidden = false;

      const total = list.children.length;
      const left = hidden.length - batch;
      if (progress) progress.textContent = `Showing ${total - Math.max(left, 0)} of ${total} collections`;
      if (left <= 0) more.remove();
      return;
    }

    if (button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    try {
      const response = await fetch(href);
      if (!response.ok) throw new Error(String(response.status));

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelectorAll('[ref="list"] > *');
      // A page with no list in it (a redirect, an error page) is not something to splice in.
      if (!items.length) throw new Error('empty');
      list.append(...items);

      const nextButton = doc.querySelector('[ref="button"]');
      const nextProgress = doc.querySelector('[ref="progress"]');
      if (nextProgress && progress) progress.textContent = nextProgress.textContent;

      if (nextButton) {
        button.setAttribute('href', nextButton.getAttribute('href') ?? href);
      } else {
        more.remove();
      }
    } catch {
      // Anything unexpected: let the link do its normal job.
      window.location.href = href;
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('oudh-load-more')) {
  customElements.define('oudh-load-more', OudhLoadMore);
}
